import React from 'react';
import PropTypes from 'prop-types';
import { Box, Stack, Typography } from '@mui/material';
import ConfigCard from './ConfigCard';
import { CheckboxTile } from './_PdiHelpers';

// All 24 legacy fields the storefront's product detail page knows. Keys are
// the column-style names used in SHOW_FIELDS_ON_DETAIL_PAGE (CSV) and in
// detail.component.html's productDetailDisplay lookups. Two labels deviate
// from naive 1:1 translation:
//   - `brand` shows as "Leverancier" (Supplier) — legacy admin convention
//   - `color_brand` shows as "Variant"
const GROUPS = [
    {
        key: 'identity',
        i18nKey: 'cfg_pdi_details_group_identity',
        fallback: 'Identity',
        fields: [
            { key: 'id',          i18nKey: 'cfg_pdi_details_id',          fallback: 'ID (number)' },
            { key: 'brand',       i18nKey: 'cfg_pdi_details_brand',       fallback: 'Supplier'    },
            { key: 'name',        i18nKey: 'cfg_pdi_details_name',        fallback: 'Name'        },
            { key: 'reference',   i18nKey: 'cfg_pdi_details_reference',   fallback: 'Reference'   },
            { key: 'color',       i18nKey: 'cfg_pdi_details_color',       fallback: 'Color'       },
            { key: 'color_brand', i18nKey: 'cfg_pdi_details_color_brand', fallback: 'Variant'     },
            { key: 'season',      i18nKey: 'cfg_pdi_details_season',      fallback: 'Season'      },
        ],
    },
    {
        key: 'categories',
        i18nKey: 'cfg_pdi_details_group_categories',
        fallback: 'Categories',
        fields: [1,2,3,4,5,6,7].map((n) => ({
            key:      `category_${n}`,
            i18nKey:  `cfg_pdi_details_category_${n}`,
            fallback: `Category ${n}`,
        })),
    },
    {
        key: 'descriptions',
        i18nKey: 'cfg_pdi_details_group_descriptions',
        fallback: 'Descriptions & Details',
        fields: [
            ...[1,2,3,4,5].map((n) => ({
                key:      `description_${n}`,
                i18nKey:  `cfg_pdi_details_description_${n}`,
                fallback: `Description ${n}`,
            })),
            ...[1,2,3,4,5].map((n) => ({
                key:      `detail_${n}`,
                i18nKey:  `cfg_pdi_details_detail_${n}`,
                fallback: `Details ${n}`,
            })),
        ],
    },
];

const ProductDetailsListCard = ({ value = {}, onChange, shopFieldLabels, theme, t }) => {
    const tr = (key, fallback) => (typeof t === 'function' ? t(key) : fallback);
    const setField = (groupKey, fieldKey, checked) => {
        if (!onChange) return;
        onChange(groupKey, { ...(value[groupKey] || {}), [fieldKey]: checked });
    };

    // Per-shop CATEGORY_N labels (from the storefront's i18n table) override
    // the generic "Categorie N" label when present. Description/Detail keys
    // have no equivalent i18n entry so they always fall through to the
    // generic label.
    const labelFor = (field) => {
        const shopLabel = shopFieldLabels && shopFieldLabels[field.key];
        return shopLabel || tr(field.i18nKey, field.fallback);
    };

    return (
        <ConfigCard
            anchor="cfg-card-pdi-details"
            title={tr('cfg_pdi_details_title', 'Product details list')}
            description={tr('cfg_pdi_details_desc', 'Pick which product attributes are visible to shoppers in the details list on the product page.')}
            theme={theme}
        >
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' }, gap: 3 }}>
                {GROUPS.map((group) => (
                    <Box key={group.key}>
                        <Typography variant="body2" sx={{ fontWeight: 700, color: 'text.primary', display: 'block', mb: 1 }}>
                            {tr(group.i18nKey, group.fallback)}
                        </Typography>
                        <Stack spacing={0.75}>
                            {group.fields.map((field) => {
                                const groupVal = value[group.key] || {};
                                return (
                                    <CheckboxTile
                                        key={field.key}
                                        label={labelFor(field)}
                                        checked={!!groupVal[field.key]}
                                        onChange={(v) => setField(group.key, field.key, v)}
                                    />
                                );
                            })}
                        </Stack>
                    </Box>
                ))}
            </Box>
        </ConfigCard>
    );
};

ProductDetailsListCard.propTypes = {
    value:              PropTypes.object,
    onChange:           PropTypes.func,
    shopFieldLabels: PropTypes.object,
    theme:              PropTypes.string,
    t:                  PropTypes.func,
};

export default ProductDetailsListCard;
